import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { getTrans, TranslationKey } from 'modules/i18n';
import { NOTE_MAX_LENGTH } from '../constants';
import { apiErrorMessage } from '../hooks';
import {
  useCreateSemesterBreakMutation,
  useUpdateSemesterBreakMutation
} from '../query';
import { AvailabilityDay, SemesterBreak } from '../types';
import { toFullWeek, toUpdateDays, validateWeekRanges } from '../utils';
import { DayPatternEditor } from './DayPatternEditor';
import { EditorDialog } from './EditorDialog';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Break being edited; null creates a new one. */
  existing: SemesterBreak | null;
  templateDays: AvailabilityDay[];
  onSaved: () => void;
}

/**
 * Creates or edits a semester break: exact dates plus the break-time weekly
 * pattern that temporarily replaces the standing template. Dates of a break
 * with a live hours request are locked server-side.
 */
export const SemesterBreakDialog = ({
  open,
  onClose,
  existing,
  templateDays,
  onSaved
}: Props) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [note, setNote] = useState('');
  const [days, setDays] = useState<AvailabilityDay[]>(toFullWeek([]));
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateSemesterBreakMutation();
  const updateMutation = useUpdateSemesterBreakMutation();
  const saving = createMutation.isLoading || updateMutation.isLoading;

  useEffect(() => {
    if (open) {
      setStartDate(existing?.startDate ?? '');
      setEndDate(existing?.endDate ?? '');
      setNote(existing?.note ?? '');
      // A new break starts from the standing template — "during my break I can
      // usually work more" edits from a familiar baseline.
      setDays(toFullWeek(existing ? existing.days : templateDays));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing?.refId]);

  const handleSave = () => {
    if (!startDate || !endDate) {
      setError(getTrans('availabilityPage.validation.break.dates-required'));
      return;
    }
    if (endDate < startDate) {
      setError(getTrans('availabilityPage.validation.break.date-range'));
      return;
    }
    const validationError = validateWeekRanges(days);
    if (validationError) {
      setError(
        getTrans(
          `availabilityPage.validation.${validationError}` as TranslationKey
        )
      );
      return;
    }

    const body = {
      startDate,
      endDate,
      note: note.trim() || null,
      days: toUpdateDays(days)
    };
    const options = {
      onSuccess: () => {
        onSaved();
        onClose();
      },
      onError: (responseError: unknown) =>
        setError(apiErrorMessage(responseError))
    };

    if (existing) {
      updateMutation.mutate({ refId: existing.refId, body }, options);
    } else {
      createMutation.mutate(body, options);
    }
  };

  return (
    <EditorDialog
      open={open}
      title={getTrans(
        existing
          ? 'availabilityPage.breakEditor.editTitle'
          : 'availabilityPage.breakEditor.addTitle'
      )}
      onClose={onClose}
      onSave={handleSave}
      saving={saving}
      dataTestId="break-editor"
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {getTrans('availabilityPage.breakEditor.hint')}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="break-editor-error">
          {error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          type="date"
          fullWidth
          label={getTrans('availabilityPage.breakEditor.startLabel')}
          value={startDate}
          onChange={event => setStartDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'data-testid': 'break-start-date' }}
        />
        <TextField
          type="date"
          fullWidth
          label={getTrans('availabilityPage.breakEditor.endLabel')}
          value={endDate}
          onChange={event => setEndDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'data-testid': 'break-end-date' }}
        />
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {getTrans('availabilityPage.breakEditor.patternTitle')}
      </Typography>
      <DayPatternEditor
        days={days}
        onChange={setDays}
        dataTestId="break-pattern-editor"
      />
      <TextField
        fullWidth
        sx={{ mt: 2 }}
        label={getTrans('availabilityPage.breakEditor.noteLabel')}
        value={note}
        onChange={event => setNote(event.target.value)}
        inputProps={{ maxLength: NOTE_MAX_LENGTH, 'data-testid': 'break-note' }}
      />
    </EditorDialog>
  );
};
